
import api from "../../../../api/axios";

/**
 * Get Title & Escrow cost calculation from backend.
 *
 * This service sends the seller-side input (`payload`) to the backend
 * API, which runs the actual calculation and returns the computed
 * title & escrow costs for the transaction.
 *
 * @param {Object} payload - Required body data for calculation (loan amount, purchase price, state, etc.).
 * @param {Object} params - Optional query params (e.g. `{ mainid, userid, companyid }`)
 *                          that can be used by the backend for user / company context.
 * @returns {Promise<Object>} - Resolved with the calculated costs from the API.
 */
export const getTitleEscrowCosts = async (payload, params = {}) => {
  try {
   // const { mainid, userid, companyid } = params;

    // Call backend to perform the actual title & escrow cost calculation.
    // `payload` contains all input fields from the seller calculator form.
    const res = await api.post(`/seller-title-escrow-costs`, payload);

    //console.log("getTitleEscrowCosts response:", res.data);
    return res.data;
  } catch (error) {
    console.error("getTitleEscrowCosts error:", error);
    // Re-throw so UI layers can handle showing error messages / fallbacks.
    throw error;
  }
};

/**
 * Fetch state-level configuration used in seller calculator.
 *
 * This typically includes per-state rules, fees, or toggles that
 * impact how title & escrow costs are calculated for that state.
 *
 * @param {Object} params - Optional query params (e.g. `{ stateCode, companyid }`).
 * @returns {Promise<Object>} - State-specific settings from backend.
 */
export const getStateSettings = async (params = {}) => {
  try {
    // GET request with optional query parameters for filtering state settings.
    const res = await api.get(`/state-setting`, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch national-level configuration used in seller calculator.
 *
 * These settings apply across all states (global rules, base fees, etc.)
 * and are combined with state-level settings to complete the calculation.
 *
 * @param {Object} params - Optional query params for scoping national settings.
 * @returns {Promise<Object>} - National/global settings from backend.
 */
export const getNationalSettings = async (params = {}) => {
  try {
    // GET request for retrieving global/national configuration.
    const res = await api.get(`/national`, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getCityStateZipCode = async (payload) => {
  try {
    const res = await api.get(`/get-city-state-for-zip?zip=${encodeURIComponent(payload.zip)}`);
    return res.data;
  } catch (error) {
    // If backend returns a structured error body (e.g., 400 for invalid ZIP),
    // pass it through so callers can show a friendly message.
    if (error?.response?.data) {
      return error.response.data;
    }
    throw error;
  }
};
